// ---------------------------------------------------------------------------
// Singleton per-region HubSpot v2 embed-script loader.
//
// Why a Map (not a Set + ad-hoc promises): a second component instance that
// is constructed BEFORE `script.onload` fires must `await` the SAME in-flight
// promise. A Set-based "loaded" flag would let the second instance call
// `hbspt.forms.create` before `window.hbspt` is defined and crash.
//
// Why `doc.createElement` (not an inline template `<script>`): per the
// project-wide rule established in STATE.md decision 27-02, Angular's
// compiler strips inline `<script>` elements as a security measure. Runtime
// DOM creation is the canonical pattern (same as `core/seo/jsonld/inject.ts`).
//
// Server-safe: this function is only invoked from `afterNextRender`
// callbacks, which Angular guarantees do NOT execute during SSR.
// ---------------------------------------------------------------------------

export type HubSpotRegion = 'eu1' | 'na1';

const inflight = new Map<HubSpotRegion, Promise<void>>();

export function loadHubSpotScript(doc: Document, region: HubSpotRegion): Promise<void> {
  const cached = inflight.get(region);
  if (cached) return cached;

  const p = new Promise<void>((resolve, reject) => {
    // If the script tag already exists (HMR / navigation replay), assume loaded.
    const existing = doc.querySelector(`script[data-hs-region="${region}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = doc.createElement('script');
    s.charset = 'utf-8';
    s.type = 'text/javascript';
    s.async = true;
    s.src = `https://js-${region}.hsforms.net/forms/embed/v2.js`;
    s.setAttribute('data-hs-region', region);
    s.onload = () => resolve();
    s.onerror = () => {
      inflight.delete(region);
      reject(new Error(`HubSpot script failed for ${region}`));
    };
    doc.head.appendChild(s);
  });

  inflight.set(region, p);
  return p;
}
